import { Button, CheckBox, Icon, Pill, TextBox } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { ReactElement, useEffect, useRef } from 'react';
import CheckboxValueGroup from '../../components/CheckboxValueGroup/CheckboxValueGroup';
import FieldGroupInCell from '../../components/FieldGroupInCell/FieldGroupInCell';
import Grid, { GridRenderer, GridRendererProps } from '../../components/Grid/Grid';
import { useStore } from '../../hooks/use-store';
import { checkSinglePermission } from '../../security/security-hook';
import {
  PERMISSION_ALLOW,
  RESOURCE_USER_MANAGEMENT_DELETE_USER,
  RESOURCE_USER_MANAGEMENT_UPDATE_USER,
} from '../../types/users-types';

const PHONE_MAX_LENGTH = 10;

export interface ManagedUser {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  blocked?: boolean;
  createdAt?: string;
  lastAccessAt?: string;
  roleIndices: number[];
  sapRoleIndices: Record<string, number[]>;
}

interface ActionsType extends GridRendererProps<ManagedUser> {
  expandedRowIds?: string[];
  action: (row: any, index: any) => void;
}

interface RowCellRendererProps extends GridRendererProps<ManagedUser> {
  field: keyof ManagedUser;
}

const RowCellRenderer = observer((props: RowCellRendererProps) => {
  const { row, index, field } = props;
  const { usersManagementStore: store, globalStore } = useStore();
  const { currentUser, currentUserIndex, loaders, currentUserError } = store;

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (field === 'firstName' || index === 0) {
      inputRef.current?.focus();
    }
  }, [field, index]);

  const changeValue = (fieldToChange: keyof ManagedUser, value: string): void => {
    if (
      fieldToChange === 'roleIndices' ||
      fieldToChange === 'sapRoleIndices' ||
      fieldToChange === 'createdAt' ||
      fieldToChange === 'lastAccessAt' ||
      fieldToChange === 'blocked' ||
      (fieldToChange === 'phone' && !/^\d*$/.test(value))
    )
      return;
    runInAction(() => {
      currentUser[fieldToChange] = value;
    });
  };
  const checkBoxDisabled = (checkBoxIndex: number): boolean => {
    if (loaders.userAction) {
      return true;
    }
    const appUser = globalStore.root.apiStore.user;
    return (
      appUser?.email === currentUser.email &&
      (appUser?.globalRoles?.some(gr => gr.name === store.globalRoles[checkBoxIndex].name) || false)
    );
  };

  if (
    currentUserIndex === index &&
    field !== 'roleIndices' &&
    field !== 'sapRoleIndices' &&
    field !== 'email' &&
    field !== 'blocked'
  ) {
    return (
      <FieldGroupInCell
        htmlFor={`table-cell-text-box_${field}`}
        errorText={index === currentUserIndex ? currentUserError[field] : undefined}
      >
        <TextBox
          ref={inputRef}
          id={`table-cell-text-box_${field}`}
          data-testid={`table-cell-text-box_${field}`}
          placeholder={field === 'phone' ? 'Add Phone' : ''}
          value={currentUser[field] || ''}
          disabled={loaders.userAction}
          maxLength={field === 'phone' ? PHONE_MAX_LENGTH : undefined}
          onChange={e => changeValue(field, e.target.value)}
        />
      </FieldGroupInCell>
    );
  }
  if (store.currentUserIndex === index && field === 'roleIndices') {
    return (
      <CheckboxValueGroup
        id='practice-roles-group'
        elementsClass='item-editors'
        variant='horizontal'
        small
        values={store.globalRoles}
        displayField='description'
        onChange={indices => store.changeRoleIndices(indices)}
        checkedIndices={store.currentUser.roleIndices}
        disabled={loaders.userAction}
        disabledByIndex={i => checkBoxDisabled(i)}
        data-testid='user-role'
      />
    );
  }
  return (
    <div className='plain-text item-editors' role='table-cell-text' aria-label={`user-${field}`}>
      {field === 'roleIndices' || field === 'sapRoleIndices'
        ? row.roleIndices
            .map(i => store.globalRoles[i])
            .filter(r => !r.hidden)
            .map(r => r.description)
            .join(', ')
        : row[field]}
    </div>
  );
});

export const ActionsRenderer = observer((props: GridRendererProps<ManagedUser>) => {
  const { index, row } = props;
  const { usersManagementStore: store, globalStore } = useStore();

  const editMode = store.currentUserIndex !== undefined;
  const disabled = store.loaders.userAction;
  const deleteUserIsAllowed = checkSinglePermission(
    globalStore.root.apiStore.userGlobalPermissions,
    RESOURCE_USER_MANAGEMENT_DELETE_USER,
    PERMISSION_ALLOW,
  );
  const updateUserIsAllowed = checkSinglePermission(
    globalStore.root.apiStore.userGlobalPermissions,
    RESOURCE_USER_MANAGEMENT_UPDATE_USER,
    PERMISSION_ALLOW,
  );
  if (store.currentUserIndex === index)
    return (
      <>
        <Button
          variant='link'
          iconOnly='save'
          size='small'
          disabled={disabled}
          onClick={() => void store.validateAndUpdateUser()}
          title='Save changes'
          aria-label='Save Changes'
          data-testid={`save-${row.email}-btn`}
        />
        <Button
          variant='link'
          iconOnly='cancel'
          size='small'
          disabled={disabled}
          onClick={() => store.cancelEditingUser()}
          title='Cancel changes'
          aria-label='Cancel Changes'
          data-testid={`cancel-${row.email}-btn`}
        />
      </>
    );
  return (
    <>
      <Button
        variant='link'
        iconOnly='edit'
        size='small'
        disabled={disabled || !updateUserIsAllowed || editMode}
        onClick={() => store.editUser(index)}
        title='Edit user'
        aria-label='Edit User'
        data-testid={`edit-${row.email}-btn`}
      />
      <Button
        variant='link'
        iconOnly='delete'
        size='small'
        disabled={disabled || !deleteUserIsAllowed || editMode}
        onClick={() => store.deleteUser(row.email)}
        title='Delete user'
        aria-label='Delete User'
        data-testid={`delete-${row.email}-btn`}
      />
    </>
  );
});

const ExpandCollapseRenderer = observer((props: ActionsType) => {
  const { row, action, expandedRowIds, index } = props;
  const iconPath = expandedRowIds?.includes(row.email) ? 'caret-up' : 'caret-down';
  const iconHint = expandedRowIds?.includes(row.email) ? 'Collapse' : 'Expand';
  return (
    <div className='actions-cell'>
      <Icon
        role='icon-button'
        className='expand-collapse-icon'
        iconName={iconPath}
        aria-label={`User ${iconHint}`}
        title={iconHint}
        onClick={() => action(row, index)}
        data-testid={`expand-${row.email}-row`}
      />
    </div>
  );
});

interface SubrowRolesRendererProps extends GridRendererProps<RoleIndicesPerSapId> {
  userEmail: string;
}

export const SubrowRolesRenderer = observer((props: SubrowRolesRendererProps) => {
  const { row, userEmail } = props;
  const store = useStore().usersManagementStore;
  const { filteredUsers, nonGlobalRoles, currentUser, loaders } = store;
  const userIndex = filteredUsers.findIndex(user => user.email === userEmail);

  if (store.currentUserIndex === userIndex) {
    const currentIndices = currentUser.sapRoleIndices[row.sapId];
    return (
      <CheckboxValueGroup
        id={`practice-roles-group-${userEmail}-${row.sapId}`}
        elementsClass='item-editors'
        variant='horizontal'
        small
        values={nonGlobalRoles}
        displayField='description'
        onChange={indices => store.changePerSapRoleIndices(row.sapId, indices)}
        checkedIndices={currentIndices}
        disabled={loaders.userAction}
        disabledByIndex={index => currentIndices.length === 1 && currentIndices[0] === index}
        data-testid='user-role'
      />
    );
  }

  return <>{row.roleIndices.map(i => nonGlobalRoles[i].description).join(', ')}</>;
});

export const SapRenderer = (props: GridRendererProps<ManagedUser>): ReactElement => (
  <>{Object.keys(props.row.sapRoleIndices).sort().join(', ')}</>
);

export const BlockedRenderer = observer((props: GridRendererProps<ManagedUser>): ReactElement => {
  const { row, index } = props;
  const { usersManagementStore: store, apiStore, globalStore } = useStore();

  if (store.currentUserIndex !== index) {
    return row.blocked ? <Pill color='negative' size='small' label='Blocked' /> : <></>;
  }

  const cbEnabled = row.email !== apiStore.userEmail && store.blockingUsersEnabled;

  const blockingAction = (email: string, blocked: boolean): void => {
    globalStore.showOkCancelPopup(
      'Update User Status',
      `Do you want to ${blocked ? 'disable' : 'enable'} ${email}?`,
      'OK',
      'Cancel',
      () => {
        runInAction(() => (store.currentUser.blocked = blocked));
      },
    );
  };

  return (
    <CheckBox
      label='Block'
      id={`user-blocked-${row.email}`}
      aria-label={`User Blocked ${row.email}`}
      size='small'
      checked={(cbEnabled ? store.currentUser.blocked : row.blocked) || false}
      disabled={!cbEnabled}
      onChange={e => blockingAction(row.email, e.target.checked)}
    />
  );
});

export const expandCollapseRenderer =
  (action: (row: ManagedUser) => void): GridRenderer<ManagedUser> =>
  (props: GridRendererProps<ManagedUser>) => (
    <ExpandCollapseRenderer
      row={props.row}
      index={props.index}
      expandedRowIds={props.expandedRowIds}
      action={action}
    />
  );

export const SubrowActionsRenderer = observer(
  (props: SubrowRolesRendererProps): React.JSX.Element => {
    const store = useStore().usersManagementStore;
    const { currentUser, loaders } = store;
    return (
      <>
        <Button
          variant='link'
          iconOnly='delete'
          size='small'
          title='Delete SAP roles'
          aria-label='Delete SAP Roles'
          disabled={loaders.userAction || currentUser.email !== props.userEmail}
          onClick={() => store.showDeleteSapModal(props.row.sapId)}
          data-testid={`delete-${props.row.sapId}-roles-btn`}
        />
      </>
    );
  },
);

export interface RoleIndicesPerSapId {
  sapId: string;
  roleIndices: number[];
}

export const SubRowRenderer = observer(
  (props: GridRendererProps<ManagedUser>): React.JSX.Element => {
    const { row, index } = props;
    const store = useStore().usersManagementStore;
    const { loaders, currentUser, currentUserIndex } = store;
    const user = currentUserIndex === index ? currentUser : row;
    const perSapRoles: RoleIndicesPerSapId[] = Object.keys(user.sapRoleIndices).map(sapId => ({
      sapId,
      roleIndices: user.sapRoleIndices[sapId],
    }));

    return (
      <div className='subrow-wrapper'>
        <Button
          className='add-more-btn'
          aria-label='Add More SAP'
          variant='link'
          size='small'
          iconLeft='plus'
          onClick={() => store.showAddSapModal()}
          disabled={currentUserIndex !== index || loaders.userAction}
        >
          Add More
        </Button>
        <Grid
          id={`users-management-grid-${user.email}`}
          className='users-management-grid-subrow'
          columns={store.subRowColDefs(user.email)}
          data={perSapRoles}
        />
      </div>
    );
  },
);

export const rowCellRenderer =
  (field: keyof ManagedUser): GridRenderer<ManagedUser> =>
  (props: GridRendererProps<ManagedUser>) => <RowCellRenderer {...props} field={field} />;

export const subrowRolesRenderer =
  (userEmail: string): GridRenderer<RoleIndicesPerSapId> =>
  (props: GridRendererProps<RoleIndicesPerSapId>) => (
    <SubrowRolesRenderer {...props} userEmail={userEmail} />
  );

export const subrowActionsRenderer =
  (userEmail: string): GridRenderer<RoleIndicesPerSapId> =>
  (props: GridRendererProps<RoleIndicesPerSapId>) => (
    <SubrowActionsRenderer {...props} userEmail={userEmail} />
  );
